// ---------------------------------------------------------------
// NotificationBell.js — Campana del portal: recordatorios de
// eventos que están a punto de empezar y mensajes enviados por el
// administrador. El admin puede redactar y enviar notificaciones
// a todo el equipo o a una persona concreta.
// ---------------------------------------------------------------
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNotifications } from "../../hooks/useNotifications";
import { useUsers } from "../../hooks/useUsers";
import { sendNotification } from "../../services/notificationService";
import Icon from "../ui/Icon";

const IconBell = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 9a6 6 0 1 0-12 0c0 6-2.5 7-2.5 7h17S18 15 18 9" />
    <path d="M10 20a2.2 2.2 0 0 0 4 0" />
  </svg>
);

function timeAgo(date) {
  if (!date) return "";
  const mins = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (mins < 1) return "ahora";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

/** Modal del admin para redactar y enviar una notificación */
function SendNotificationModal({ fromName, onClose, onSent }) {
  const { users } = useUsers();
  const [toUid, setToUid] = useState("all");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Ponle un título a la notificación.");
      return;
    }
    setError("");
    setSending(true);
    try {
      await sendNotification({ toUid, title, body, fromName });
      onSent();
      onClose();
    } catch (err) {
      console.error(err);
      setError(
        err.code === "permission-denied"
          ? "Sin permisos: republica las reglas de Firestore (colección notifications)."
          : "No se pudo enviar la notificación."
      );
      setSending(false);
    }
  };

  // Se renderiza en el <body> (fuera de la topbar): así el modal se
  // centra en la pantalla y no queda anclado a la barra superior, que
  // al llevar cristal esmerilado actúa como contenedor de los fixed.
  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2>Enviar notificación</h2>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar">
            <Icon name="close" />
          </button>
        </header>

        <form className="modal-form" onSubmit={handleSubmit}>
          <label className="modal-field">
            <span>Destinatario</span>
            <select
              className="modal-input"
              value={toUid}
              onChange={(e) => setToUid(e.target.value)}
            >
              <option value="all">Todo el equipo</option>
              {users.map((u) => (
                <option key={u.uid} value={u.uid}>
                  {u.username || u.displayName}
                </option>
              ))}
            </select>
          </label>

          <label className="modal-field">
            <span>Título</span>
            <input
              className="modal-input"
              type="text"
              placeholder="p. ej. Cambio de turno el viernes"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
            />
          </label>

          <label className="modal-field">
            <span>Mensaje</span>
            <textarea
              className="modal-input modal-textarea"
              placeholder="Escribe el mensaje…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
            />
          </label>

          {error && <div className="modal-error">{error}</div>}

          <footer className="modal-footer">
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={sending}>
 {sending ? "Enviando…":"Enviar"}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>,
    document.body
  );
}

/** Qué icono lleva cada tipo de aviso */
const ICONO_AVISO = {
  task: "clipboard",
  clock: "clock",
  reminder: "bell",
  swap: "repeat",
  note: "pin",
  message: "send",
};

export default function NotificationBell({ user, profile, isAdmin, onNavigate }) {
  const { items, unread, markRead, markAllRead, removeItem, requestPermission } =
    useNotifications(user, isAdmin);
  const [open, setOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sentMsg, setSentMsg] = useState(false);

  // Contador en la pestaña del navegador: "(3) Calendario · …"
  useEffect(() => {
    const base = "Calendario · Farmacia Buendía";
    document.title = unread > 0 ? `(${unread}) ${base}` : base;
    return () => {
      document.title = base;
    };
  }, [unread]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) requestPermission();
  };

  return (
    <div className="notif-wrap">
      <button
        className="notif-btn"
        onClick={toggle}
        aria-label="Notificaciones"
        title="Notificaciones"
      >
        <IconBell />
        {unread > 0 && <span className="notif-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <>
          <div className="notif-overlay" onClick={() => setOpen(false)} />
          <div className="notif-panel card">
            <div className="notif-head">
              <span className="card-title">Notificaciones</span>
              {unread > 0 && (
                <button
                  className="btn-secondary notif-readall"
                  onClick={markAllRead}
                  title="Marcar todas como leídas"
                >
                  <Icon name="check" size={14} /> Marcar leídas
                </button>
              )}
              {isAdmin && (
                <button
                  className="btn-primary notif-send-btn"
                  onClick={() => {
                    setSendOpen(true);
                    setOpen(false);
                  }}
                >
                  + Enviar
                </button>
              )}
            </div>

            <div className="notif-list">
              {items.map((n) => (
                <div
                  key={n.id}
                  className={`notif-item ${n.unread ? "unread" : ""} ${n.link ? "clickable" : ""}`}
                  onClick={
                    n.link
                      ? () => {
                          setOpen(false);
                          // Un mensaje que se abre ya está leído (así el
                          // de cambios de horario empieza de cero la
                          // próxima vez, en vez de seguir sumando)
                          if (n.type === "message" && n.unread) markRead(n);
                          onNavigate?.(n.link);
                        }
                      : undefined
                  }
                >
                  {/* Icono de verdad, no un emoji: los emojis los
                      dibuja cada sistema a su manera (y varios de los
                      que había aquí se habían quedado en blanco). */}
                  <span className={`notif-icon tipo-${n.type}`}>
                    <Icon name={ICONO_AVISO[n.type] || "bell"} size={16} />
                  </span>
                  <div className="notif-info">
                    <div className="notif-title">{n.title}</div>
                    {n.body && <div className="notif-body">{n.body}</div>}
                    <div className="notif-meta">
                      {n.type === "message"
                        ? `${n.from || "Administración"} · ${timeAgo(n.at)}`
                        : n.type === "swap"
                        ? "Intercambio de turnos"
                        : n.type === "task"
                        ? "Tarea de turno · Hoy"
                        : n.type === "clock"
                        ? "Fichaje · Hoy"
                        : "Hoy"}
                    </div>
                  </div>
                  <div className="notif-actions" onClick={(e) => e.stopPropagation()}>
                    {n.unread && n.dismissible !== false && (
                      <button
                        className="notif-action"
                        onClick={() => markRead(n)}
                        title="Marcar como leída"
                        aria-label="Marcar como leída"
                      >
                        <Icon name="check" />
                      </button>
                    )}
                    <button
                      className="notif-action danger"
                      onClick={() => removeItem(n)}
                      title="Borrar notificación"
                      aria-label="Borrar notificación"
                    >
                      <Icon name="trash" />
                    </button>
                  </div>
                </div>
              ))}
              {items.length === 0 && (
                <div className="empty-state">
                  <Icon name="bell" size={26} />
                  <span className="empty-state-text">
                    No tienes notificaciones.
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {sendOpen && (
        <SendNotificationModal
          fromName={profile?.username || profile?.displayName || "Administración"}
          onClose={() => setSendOpen(false)}
          onSent={() => {
            setSentMsg(true);
            setTimeout(() => setSentMsg(false), 4000);
          }}
        />
      )}
    </div>
  );
}
